// components/MessageList.tsx
import React from 'react';
import { Message } from './ChatInterface';
import { Theme } from '../types/theme';
import AudioControls from './AudioControls';
import MarkdownRenderer from './MarkdownRenderer';
import { chatService } from '../api/chatService';
import { ViewMode } from '../types';
import FileUpload from './chat_components/FileUpload';

interface MessageListProps {
  messages: Message[];
  theme: Theme;
  viewMode: ViewMode;
  agentName?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  theme,
  viewMode,
  agentName
}) => {
  if (messages.length === 0) return null;

  return (
    <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
      {messages.map((message) => {
        const isUser = message.type === 'user';
        const isAssistant = message.type === 'assistant';
        return (
          <div
            key={message.id}
            className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'
              } items-start group`}
          >
            <div
              className={`flex flex-col ${isUser ? 'items-end' : 'items-start'
                } ${isUser
                  ? 'max-w-[70%]'
                  : viewMode === 'widget'
                    ? 'flex-1'
                    : 'max-w-[75%]'
                }`}
            >
              <div
                className={`flex items-center gap-2 mb-1 px-1 ${isUser ? 'flex-row-reverse' : 'flex-row'
                  }`}
              >
              </div>
              {(message.content || message.error) && (
                <div
                  className={`rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md ${isUser ? 'rounded-tr-md' : 'rounded-tl-md'
                    }`}
                  style={{
                    backgroundColor: isUser
                      ? theme.primaryColor
                      : theme.surfaceColor,
                    color: isUser ? '#ffffff' : theme.textColor,
                    padding: '8px 12px',

                    border: isAssistant
                      ? `1px solid ${theme.borderColor}`
                      : 'none',
                  }}
                >
                  {/* File Upload */}
                  {message.files && message.files.length > 0 && (
                    <div className="mb-3">
                      <FileUpload
                        files={message.files}
                        onRemove={() => { }}
                        theme={theme}
                      />
                    </div>
                  )}

                  {/* Text Content */}
                  <div className="text-sm leading-relaxed font-medium prose prose-sm prose-p:m-0">
                    {isAssistant ? (
                      <div className="prose-p:m-0 prose-ul:m-0 prose-li:m-0">
                        <MarkdownRenderer content={message.content} theme={theme} />
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    )}
                  </div>

                  {/* Error */}
                  {message.error && (
                    <div className="px-4 pb-3">
                      <div
                        className="text-xs mt-2 px-3 py-2 rounded-lg"
                        style={{
                          color: '#ef4444',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        }}
                      >
                        ⚠ {message.error}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 🔄 Loading dots - MOVED OUTSIDE THE BUBBLE */}
              {message.isLoading && isAssistant && (
                <div className="flex items-center space-x-2 px-1 py-1 mt-2">
                  <div className="flex space-x-1.5">
                    {[0, 150, 300].map((delay, i) => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{
                          backgroundColor: theme.primaryColor,
                          animationDelay: `${delay}ms`,
                          opacity: 0.6,
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: theme.textSecondaryColor }}
                  >
                    Thinking...
                  </span>
                </div>
              )}

              {/* Custom Component (buttons, etc.) */}
              {message.component && (
                <div className="mt-3 w-full">{message.component}</div>
              )}

              {/* Audio Controls */}
              {isAssistant &&
                message.content &&
                !message.isLoading &&
                !message.error && (
                  <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <AudioControls
                      theme={theme}
                      textToSpeak={message.content}
                      onAudioAction={(() => {
                        let audio: HTMLAudioElement | null = null;
                        let currentUrl: string | null = null;

                        return (
                          action: 'start' | 'stop',
                          callbacks?: { onPlayStart: () => void; onEnd: () => void }
                        ) => {
                          if (action === 'stop') {
                            if (audio) {
                              try {
                                audio.pause();
                                audio.currentTime = 0;
                              } catch { }
                              audio = null;
                            }
                            if (currentUrl) {
                              try {
                                URL.revokeObjectURL(currentUrl);
                              } catch { }
                              currentUrl = null;
                            }
                            return;
                          }

                          // action === 'start'
                          const mediaSource = new MediaSource();
                          const url = URL.createObjectURL(mediaSource);
                          audio = new Audio(url);
                          currentUrl = url;

                          mediaSource.addEventListener('sourceopen', async () => {
                            try {
                              const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');

                              // Signal start immediately when we request the stream
                              callbacks?.onPlayStart();

                              const stream = await chatService.streamTextToSpeech(message.content!, 'Mason-PlayAI', 'mp3');
                              const reader = stream.getReader();

                              audio?.play().catch(() => {
                                // Ignore auto-play errors or interruptions
                              });

                              const pump = async () => {
                                try {
                                  const { done, value } = await reader.read();
                                  if (done) {
                                    if (mediaSource.readyState === 'open') {
                                      mediaSource.endOfStream();
                                    }
                                    return;
                                  }

                                  if (value) {
                                    sourceBuffer.appendBuffer(value as BufferSource);

                                    // Strictly wait for update to finish before continuing
                                    await new Promise<void>((resolve) => {
                                      sourceBuffer.addEventListener('updateend', () => resolve(), { once: true });
                                    });
                                  }

                                  // Continue reading next chunk
                                  await pump();
                                } catch (err) {
                                  console.error('Error reading stream:', err);
                                  if (mediaSource.readyState === 'open') {
                                    mediaSource.endOfStream();
                                  }
                                }
                              };

                              await pump();

                            } catch (err) {
                              console.error('MediaSource/Streaming error:', err);
                              callbacks?.onEnd();
                            }
                          });

                          audio.onended = () => {
                            if (currentUrl) {
                              URL.revokeObjectURL(currentUrl);
                              currentUrl = null;
                            }
                            audio = null;
                            callbacks?.onEnd();
                          };

                          audio.onerror = () => {
                            console.error('Audio playback error', audio?.error);
                            if (currentUrl) {
                              URL.revokeObjectURL(currentUrl);
                              currentUrl = null;
                            }
                            audio = null;
                            callbacks?.onEnd();
                          }
                        };
                      })()}
                    />
                  </div>
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageList;